import { useEffect, useState } from "react";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

const DISMISS_KEY = "fideliza:install_banner_dismissed";

export function InstallBanner() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    setIsStandalone(
      window.matchMedia("(display-mode: standalone)").matches ||
        // @ts-expect-error iOS
        window.navigator.standalone === true
    );
    setIsIOS(/iPad|iPhone|iPod/.test(navigator.userAgent));
    setDismissed(sessionStorage.getItem(DISMISS_KEY) === "1");

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  if (isStandalone || dismissed) return null;
  if (!deferred && !isIOS) return null;

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  const install = async () => {
    if (!deferred) return;
    await deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice.outcome === "accepted") {
      setDeferred(null);
    }
  };

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-3 z-40 flex justify-center px-3">
      <div className="pointer-events-auto flex w-full max-w-md items-start gap-3 rounded-2xl border border-leaf-100 bg-white p-3 shadow-lg">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-leaf-50">
          <svg className="h-5 w-5 text-leaf-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 3v12" /><path d="m6 9 6 6 6-6" /><path d="M5 21h14" />
          </svg>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-leaf-950">Instala la app</p>
          <p className="text-xs text-leaf-700">
            {isIOS
              ? "Toca Compartir → 'Agregar a inicio' para tener Fideliza como app."
              : "Acceso directo desde tu pantalla de inicio."}
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          {deferred && (
            <button onClick={install} className="rounded-full bg-leaf-700 px-3 py-1.5 text-xs font-semibold text-white">
              Instalar
            </button>
          )}
          <button onClick={dismiss} className="text-xs text-leaf-500 hover:text-leaf-700">
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}
